/**
 * EditDescriptionModal — "edit the service description page" action for
 * meta-broker catalogue tiles (brief-17-admin-catalogue-tile-actions).
 *
 * KNOWN GAP: no PATCH endpoint for an existing registry-service descriptor is
 * confirmed in the graph or codebase yet (registerDraft() in useProvisioning.ts
 * only POSTs new drafts). This modal submits to the schema-consistent
 * PATCH /api/broker/v1/registry/services/:id and surfaces any non-2xx as an
 * inline error rather than assuming success — wire the real endpoint (or
 * confirm the existing one) before this ships past the test surface. See the
 * brief-17 run-record doc.
 */

import { useState, type MouseEvent } from 'react';
import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import { useAuth } from '../auth/KeycloakProvider';

interface EditDescriptionModalProps {
  tile: TileConfig;
  onClose: () => void;
  onSaved?: () => void;
}

type Phase = 'editing' | 'submitting' | 'done' | 'error';

export default function EditDescriptionModal({ tile, onClose, onSaved }: EditDescriptionModalProps) {
  const { token } = useAuth();
  const [description, setDescription] = useState(tile.description);
  const [phase, setPhase] = useState<Phase>('editing');
  const [error, setError] = useState<string | null>(null);

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget && phase !== 'submitting') onClose();
  }

  async function handleSave() {
    if (!token) return;
    setPhase('submitting');
    setError(null);
    try {
      const res = await fetch(`/api/broker/v1/registry/services/${tile.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ description }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `save failed (HTTP ${res.status})`);
      }
      setPhase('done');
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-description-title"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'var(--scrim)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--sp-8)',
      }}
    >
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border-default)',
        width: '100%', maxWidth: '520px',
        padding: 'var(--sp-8)',
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)',
        boxShadow: 'var(--shadow-2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p className="dp-eyebrow" style={{ margin: '0 0 var(--sp-1)' }}>{t('tile.editDescription.eyebrow')}</p>
            <h2 id="edit-description-title" style={{
              fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-h3)', fontWeight: 600,
              color: 'var(--fg-1)', margin: 0,
            }}>
              {tile.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={phase === 'submitting'}
            aria-label={t('serviceDetail.close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-3)', fontSize: '20px', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {(phase === 'editing' || phase === 'error') && (
          <>
            <div>
              <label htmlFor="dp-edit-description" style={{
                display: 'block', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
                fontWeight: 600, color: 'var(--fg-1)', marginBottom: 'var(--sp-1)',
              }}>
                {t('tile.editDescription.fieldLabel')}
              </label>
              <textarea
                id="dp-edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                style={{
                  width: '100%', padding: '8px var(--sp-3)',
                  borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border-default)',
                  borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
                  background: 'var(--bg-surface)', color: 'var(--fg-1)', resize: 'vertical',
                }}
              />
            </div>

            {phase === 'error' && error && (
              <p style={{ color: 'var(--dp-crimson-600)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', margin: 0 }}>
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--ghost" onClick={onClose}>{t('destructive.cancel')}</button>
              <button className="dp-btn dp-btn--primary" onClick={handleSave}>{t('tile.editDescription.save')}</button>
            </div>
          </>
        )}

        {phase === 'submitting' && (
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-3)' }}>
            {t('tile.editDescription.submitting')}
          </p>
        )}

        {phase === 'done' && (
          <>
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-2)' }}>
              {t('tile.editDescription.done')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--primary" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
